<template>
    <div class="deliverable-preview-drawer__body">
        <div class="deliverable-preview-drawer__header">
            <navigation-tabs v-if="isPublished">
                <navigation-tabs-item
                    :is-active="activeTab === PreviewDrawerTab.Preview"
                    @click="activeTab = PreviewDrawerTab.Preview"
                >
                    Production
                </navigation-tabs-item>

                <navigation-tabs-item
                    v-if="isPublishedToDoubleClick"
                    :is-active="activeTab === PLATFORM.DOUBLECLICK"
                    @click="activeTab = PLATFORM.DOUBLECLICK"
                >
                    {{ PlatformLabel[PLATFORM.DOUBLECLICK] }}
                </navigation-tabs-item>

                <navigation-tabs-item
                    v-if="isPublishedToOpenDC"
                    :is-active="activeTab === PLATFORM.OPENDC"
                    @click="activeTab = PLATFORM.OPENDC"
                >
                    {{ PlatformLabel[PLATFORM.OPENDC] }}
                </navigation-tabs-item>
            </navigation-tabs>
        </div>

        <div class="deliverable-preview-drawer__content">
            <template v-if="deliverable">
                <hox-alert
                    v-if="activeTab !== 'preview'"
                    class="deliverable-preview-drawer__feed-wrapper"
                    size="small"
                    :show-icon="false"
                    align-text="center"
                >
                    <template #content>
                        <div class="deliverable-preview-drawer__feed">
                            <a
                                ref="feedUrl"
                                class="deliverable-preview-drawer__feed-link"
                                :href="publishedStatus?.xmlFeedUrl"
                                target="_blank"
                            >
                                {{ publishedStatus?.xmlFeedUrl }}
                            </a>
                            <Button class="deliverable-preview-drawer__feed-copy" size="small" @click="copyFeedUrl">
                                {{ copyBtnText }}
                            </Button>
                        </div>
                    </template>
                </hox-alert>

                <div class="deliverable-preview-drawer__preview_wrapper">
                    <div v-if="!isAE" class="deliverable-preview-drawer__qr-side">
                        <div class="qr-box">
                            <preview-url-qr-code :deliverable="deliverable" />
                            <div class="qr-copy">Scan to view on mobile</div>
                        </div>
                    </div>

                    <div class="deliverable-preview-drawer__banner-side">
                        <div class="deliverable-preview-drawer__banner-container" :style="bannerStyle">
                            <BannerPreview
                                :id="bannerId"
                                :key="activeTab"
                                content-clickable
                                :preview-values="bannerPreviewValues"
                                :ad-type="deliverable.masterTemplate.adType"
                                :playback-namespace="playbackNamespace"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <PreviewGroupControls
            v-if="playbackController"
            class="deliverable-preview-drawer__playback-controls"
            :scrubbar="{
                currentTime: playbackController.currentTime,
                duration: playbackController.duration
            }"
            :play-pause-button="{ isPlaying: playbackController.isPlaying }"
            @playPause="playbackController.playPause"
            @setBannerPlaybackTime="playbackController.seek"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { Deliverable } from "@/types";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import { PLATFORM } from "hoxton-shared/enums/exports";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import NavigationTabs from "@/components/common/NavigationTabs/Container.vue";
import NavigationTabsItem from "@/components/common/NavigationTabs/Tab.vue";
import HoxAlert from "@/components/common/Alert.vue";
import PreviewUrlQrCode from "@/components/Campaign/PreviewUrlQrCode.vue";
import PreviewGroupControls from "@/components/PlaybackControls/PreviewGroupControls.vue";

import { PlatformLabel } from "@/enums/platforms.ts";

import URLManager from "@/services/URLManager";

import { DeliverableLibraryAction, DeliverableLibraryGetters } from "@/store/modules/deliverableLibrary";
import { getBannerIdFromDeliverable } from "@/utils/getBannerIframeId";
import { BannerNamespace, usePlaybackController } from "@/components/PlaybackControls/usePlaybackController";
import { usePreviewValues } from "@/composables/usePreviewValues";
import { PreviewDrawerTab } from "@/enums/library";

const QueryParams = {
    ActiveTab: "ddp-at"
};

export default defineComponent({
    name: "DeliverablePreviewDrawerTab",
    components: {
        PreviewUrlQrCode,
        HoxAlert,
        BannerPreview,
        NavigationTabs,
        NavigationTabsItem,
        PreviewGroupControls
    },

    props: {
        deliverable: {
            type: Object as PropType<Deliverable>,
            required: true
        }
    },

    setup() {
        const previewValues = usePreviewValues();

        return {
            previewValues
        };
    },

    data() {
        return {
            copyBtnText: "Copy URL",
            playbackNamespace: BannerNamespace.Preview,
            playbackController: null as null | ReturnType<typeof usePlaybackController>,
            PLATFORM,
            PlatformLabel,
            urlManager: null as null | URLManager,
            PreviewDrawerTab
        };
    },

    computed: {
        activeTab: {
            get() {
                return this.$store.state.deliverableLibrary.previewDrawerTab.activeTab;
            },

            set(value: (typeof PreviewDrawerTab)[keyof typeof PreviewDrawerTab] | null) {
                this.$store.dispatch(DeliverableLibraryAction.SetPreviewDrawerActiveTab, value);
            }
        },

        bannerId() {
            return getBannerIdFromDeliverable(this.deliverable, this.playbackNamespace);
        },

        bannerWidth() {
            return this.deliverable.masterTemplate.width;
        },

        bannerHeight() {
            return this.deliverable.masterTemplate.height;
        },

        bannerStyle() {
            return {
                width: `${this.bannerWidth}px`,
                height: `${this.bannerHeight}px`
            };
        },

        bannerPreviewValues() {
            if (this.activeTab !== "preview") {
                const deliverable = {
                    computedValues: this.publishedEditables,
                    metadata: this.publishedMetadata,
                    masterTemplate: this.deliverable.masterTemplate
                } as unknown as Deliverable;

                this.previewValues(deliverable);
            }

            return this.previewValues(this.deliverable);
        },

        isAE() {
            return this.deliverable.masterTemplate.adType === TemplateType.AE;
        },

        isPublished() {
            return this.isPublishedToDoubleClick || this.isPublishedToOpenDC;
        },

        isPublishedToDoubleClick() {
            if (this.deliverable) {
                return this.$store.getters[DeliverableLibraryGetters.computedDeliverableIsPublishedToDoubleClick](
                    this.deliverable.idHash
                );
            }

            return false;
        },

        isPublishedToOpenDC() {
            if (this.deliverable) {
                return this.$store.getters[DeliverableLibraryGetters.computedDeliverableIsPublishedToODC](
                    this.deliverable.idHash
                );
            }

            return false;
        },

        publishedEditables() {
            return this.publishedStatus ? this.publishedStatus.computedEditables : [];
        },

        publishedMetadata() {
            return this.publishedStatus ? this.publishedStatus.metadata : [];
        },

        publishedStatus() {
            if (!this.activeTab) {
                return;
            }

            return this.$store.state.deliverableLibrary.deliverableStatusByIdHash[this.deliverable.idHash]
                ?.publishedStatus?.[this.activeTab];
        }
    },
    watch: {
        activeTab() {
            this.setActiveTabQueryParams();
        }
    },

    beforeUnmount() {
        this.unsetAllQueryParams();
    },

    created() {
        this.urlManager = new URLManager(this.$router, {
            defaults: [
                {
                    key: QueryParams.ActiveTab,
                    value: ["preview"]
                }
            ],
            tracked: Object.values(QueryParams)
        });

        this.initActiveTab();
        this.setActiveTabQueryParams();

        this.playbackController = usePlaybackController(this.playbackNamespace);
    },
    methods: {
        copyFeedUrl() {
            const selection = window.getSelection();
            const range = document.createRange();

            try {
                if (!selection) {
                    throw new Error("");
                }

                range.selectNodeContents(this.$refs.feedUrl as Node);
                selection.removeAllRanges();
                selection.addRange(range);

                document.execCommand("copy");
                selection.removeAllRanges();

                const original = this.copyBtnText;
                this.copyBtnText = "Copied!";

                setTimeout(() => {
                    this.copyBtnText = original;
                }, 1200);
            } catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                this.$snackbar.error(message);
            }
        },

        initActiveTab() {
            if (!this.activeTab) {
                const queryParamActiveTab = this.$route.query[QueryParams.ActiveTab];
                if (queryParamActiveTab === PLATFORM.DOUBLECLICK && this.isPublishedToDoubleClick) {
                    this.activeTab = queryParamActiveTab;
                } else {
                    this.activeTab = "preview";
                }
            }
        },

        setActiveTabQueryParams() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: QueryParams.ActiveTab,
                        value: [this.activeTab]
                    }
                ],
                true
            );
        },

        unsetAllQueryParams() {
            this.urlManager?.setURLParams(
                Object.values(QueryParams).map(key => ({
                    key,
                    value: []
                })),
                true
            );
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.deliverable-preview-drawer__body {
    width: 100%;

    display: flex;
    flex-direction: column;
}

.deliverable-preview-drawer__header {
    background-color: white;
    border-bottom: 1px solid $grey2;
    padding-left: $spacing-large;

    .navigation-tab {
        padding: 24px $spacing-large 20px;
    }
}

.deliverable-preview-drawer__content {
    flex: 1;

    overflow-y: auto;
    position: relative;

    display: flex;
    flex-direction: column;
}

.deliverable-preview-drawer__preview_wrapper {
    display: flex;
}

.deliverable-preview-drawer__qr-side {
    flex: 0 0 auto;

    padding: 32px;
}

.deliverable-preview-drawer__qr-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding: $spacing $spacing $spacing-small $spacing;
    border: 1px solid $grey2;
    background-color: $white;
}

.deliverable-preview-drawer__banner-side {
    flex: 1;

    padding: 32px;

    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.deliverable-preview-drawer__banner-container {
    display: flex;
}

.deliverable-preview-drawer__playback-controls {
    flex: 0 0 auto;
}

.deliverable-preview-drawer__feed-wrapper {
    margin: $spacing $spacing-large 0 $spacing-large;
    width: auto;
}

.deliverable-preview-drawer__feed {
    display: flex;
    align-items: center;
}

.deliverable-preview-drawer__feed-link {
    color: $darkgrey2;
    padding: 0 20px;
}

.deliverable-preview-drawer__feed-copy {
    margin-left: auto;
    margin-right: 12px;

    &.ivu-btn {
        font-size: 11px;
        background-color: $grey2;
        border: none;

        &:hover {
            background-color: $grey3;
        }
    }
}
</style>
